import { useTranslation } from "react-i18next";
import Input from "./Input";

/**
 * MediaNameInput - A specialized input component for media name entry with built-in character counter and validation
 *
 * This component provides consistent UX for media name inputs across avatar generation, media upload, and media editing.
 * It displays a real-time character counter that changes color based on proximity to the limit:
 * - Gray (default): Below 50 characters
 * - Yellow (warning): 50-54 characters
 * - Red (limit): 55 characters (maximum)
 *
 * @example
 * ```tsx
 * <MediaNameInput
 *   value={mediaName}
 *   onChange={setMediaName}
 *   maxLength={55}
 * />
 * ```
 */
interface MediaNameInputProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  placeholder?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  name?: string;
}

const MediaNameInput = ({
  value,
  onChange,
  maxLength = 55,
  placeholder,
  required = true,
  className = "",
  disabled = false,
  name = "name",
}: MediaNameInputProps) => {
  const { t } = useTranslation();
  const charCount = value.length;
  const isNearLimit = charCount >= maxLength - 5;
  const isAtLimit = charCount >= maxLength;
  const isWhitespaceOnly = value.trim() === "";
  const showError = isWhitespaceOnly && charCount > 0;

  return (
    <div className={className}>
      <Input
        name={name}
        type="text"
        maxLength={maxLength}
        value={value}
        placeholder={placeholder || t("general.fileName")}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
      />
      {showError && (
        <div className="text-red-600 text-sm mt-1">
          {t("general.nameCannotBeEmpty") || "Name cannot be empty or contain only spaces"}
        </div>
      )}
      <div
        className={`text-sm mt-1 ${
          isAtLimit ? "text-red-600" : isNearLimit ? "text-yellow-600" : "text-gray-500"
        }`}
      >
        {charCount}/{maxLength} {t("general.characters") || "characters"}
      </div>
    </div>
  );
};

export default MediaNameInput;
export { MediaNameInput };
